import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';
import PageOverlayHeader from 'features/IRWorkstation/components/Layout/PageOverlayHeader';
import KPICard from 'features/IRWorkstation/components/Common/KPICard';
import StatusPill from 'features/IRWorkstation/components/Common/StatusPill';
import fakeData from 'features/IRWorkstation/constants/fakeData';

const CICard = ({ card }) => (
	<Box
		sx={{
			backgroundColor: 'background.paper',
			border: '0.5px solid',
			borderColor: 'border.light',
			borderRadius: '12px',
			padding: '14px 16px',
			display: 'flex',
			flexDirection: 'column',
			gap: '8px',
			cursor: 'pointer',
			'&:hover': {
				border: '1px solid',
				borderColor: 'primary.main',
			},
		}}
	>
		{/* header: name/sub left, pill right */}
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'flex-start',
			}}
		>
			<Box>
				<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
					{card.name}
				</Typography>
				<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
					{card.sub}
				</Typography>
			</Box>
			<StatusPill label={card.pill.label} color={card.pill.color} />
		</Box>
		{/* full-bleed divider above stats */}
		<Box
			sx={{
				width: 'calc(100% + 32px)',
				mx: '-16px',
				borderTop: '0.5px solid',
				borderColor: 'border.light',
			}}
		/>
		{/* stats rows */}
		{card.stats.map((s) => (
			<Box
				key={s.label}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
					{s.label}
				</Typography>
				{s.pill ? (
					<StatusPill label={s.pill.label} color={s.pill.color} />
				) : (
					<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
						{s.value}
					</Typography>
				)}
			</Box>
		))}
		{/* footer: full-bleed background with vdr button left, action button right */}
		<Box
			sx={{
				width: 'calc(100% + 32px)',
				mx: '-16px',
				mb: '-14px',
				px: '16px',
				py: '10px',
				borderTop: '0.5px solid',
				borderColor: 'border.light',
				backgroundColor: 'background.subtle',
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				borderRadius: '0 0 12px 12px',
			}}
		>
			{card.vdrBadge ? (
				<Box
					sx={{
						backgroundColor: 'vdr.bg',
						borderRadius: '6px',
						padding: '4px 8px',
						border: '0.5px solid',
						borderColor: 'vdr.border',
						cursor: 'pointer',
						'&:hover': { backgroundColor: 'vdr.hover' },
					}}
				>
					<Typography
						sx={{ fontSize: '10px', fontWeight: 600, color: 'vdr.text' }}
					>
						{card.vdrBadge}
					</Typography>
				</Box>
			) : (
				<Box />
			)}
			<Button
				variant='outlined'
				size='small'
				sx={{ borderColor: 'status.blue.border', color: 'status.blue.text' }}
			>
				{card.actionLabel}
			</Button>
		</Box>
	</Box>
);

CICard.propTypes = {
	card: PropTypes.object.isRequired,
};

const CoInvestDashboard = ({ onBack }) => {
	const { coInvestKPIs, coInvestCards } = fakeData;

	return (
		<Box
			sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
		>
			<PageOverlayHeader
				title='Co-Invest Dashboard'
				onBack={onBack}
				meta='3 active · $285M exposure'
				endButtons={
					<Box
						component='span'
						sx={{
							fontSize: '10px',
							fontWeight: 600,
							color: 'vdr.text',
							backgroundColor: 'vdr.bg',
							border: '0.5px solid',
							borderColor: 'vdr.border',
							borderRadius: '3px',
							padding: '1px 6px',
							cursor: 'pointer',
							'&:hover': { backgroundColor: 'vdr.hover' },
						}}
					>
						↔ Open Raise VDR for deal rooms
					</Box>
				}
			/>
			<Box
				sx={{
					flex: 1,
					overflowY: 'auto',
					padding: '16px 20px',
					backgroundColor: 'background.default',
					display: 'flex',
					flexDirection: 'column',
					gap: '14px',
				}}
			>
				{/* kpi strip */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: 'repeat(4, 1fr)',
						gap: '10px',
					}}
				>
					{coInvestKPIs.map((kpi) => (
						<KPICard key={kpi.label} {...kpi} />
					))}
				</Box>

				{/* ci cards */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: 'repeat(3, 1fr)',
						gap: '12px',
					}}
				>
					{coInvestCards.map((card) => (
						<CICard key={card.name} card={card} />
					))}
				</Box>
			</Box>
		</Box>
	);
};

CoInvestDashboard.propTypes = {
	onBack: PropTypes.func.isRequired,
};

export default CoInvestDashboard;
